import React from "react";
import * as pdfjsLib from "pdfjs-dist";
import { X, ExternalLink, Loader2, AlertTriangle } from "lucide-react";

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

// In-app XFA (Adobe dynamic form) preview. pdf.js renders XFA in the browser (view-grade) so operators SEE the
// form instead of Adobe's "Please wait" placeholder. Fill/sign still happens in Adobe Reader before submission.
export function XfaPreview({ dealId, formKey, title, onClose }) {
  const containerRef = React.useRef(null);
  const [state, setState] = React.useState("loading"); // loading | ready | error

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const url = `${API_BASE}/bids/${dealId}/forms/file/${formKey}`;
        const resp = await fetch(url, { credentials: "include" });
        const buf = await resp.arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data: buf, enableXfa: true }).promise;
        if (cancelled) return;
        const host = containerRef.current;
        if (host) host.replaceChildren();
        for (let n = 1; n <= pdf.numPages; n++) {
          const page = await pdf.getPage(n);
          const viewport = page.getViewport({ scale: 1.4 });
          const canvas = document.createElement("canvas");
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          canvas.className = "mx-auto mb-4 shadow border border-zinc-200 bg-white";
          host.appendChild(canvas);
          await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise;
        }
        if (!cancelled) setState("ready");
      } catch (e) {
        console.error("XFA preview failed", e);
        if (!cancelled) setState("error");
      }
    })();
    return () => { cancelled = true; };
  }, [dealId, formKey]);

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="xfa-preview-modal">
      <div className="bg-zinc-50 rounded-lg w-full max-w-4xl h-[88vh] flex flex-col">
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-zinc-200 bg-white rounded-t-lg">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-zinc-800 truncate">{title || "Adobe XFA form"}</div>
            <div className="text-[11px] text-amber-700 flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" /> View-grade preview — review &amp; sign in Adobe Reader before submission.
            </div>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <a href={`${API_BASE}/bids/${dealId}/forms/file/${formKey}`} target="_blank" rel="noreferrer"
              data-testid="xfa-download-prefilled" className="text-[11px] text-zinc-600 hover:text-zinc-900 flex items-center gap-1">
              <ExternalLink className="h-3 w-3" /> Open pre-filled
            </a>
            <button onClick={onClose} data-testid="xfa-preview-close" className="text-zinc-500 hover:text-zinc-900"><X className="h-4 w-4" /></button>
          </div>
        </div>
        <div className="flex-1 overflow-auto p-4" data-testid="xfa-preview-canvas">
          {state === "loading" && (
            <div className="h-full flex items-center justify-center text-zinc-500 text-sm">
              <Loader2 className="h-4 w-4 animate-spin mr-2" /> Rendering the form…
            </div>
          )}
          <div ref={containerRef} />
        </div>
        {state === "error" && (
          <div className="px-4 py-3 border-t border-amber-200 bg-amber-50 text-[12px] text-amber-800" data-testid="xfa-preview-error">
            Couldn't render this dynamic form in the browser. Use “Open pre-filled” and review it in the free
            {" "}<a className="underline" href="https://get.adobe.com/reader/" target="_blank" rel="noreferrer">Adobe Acrobat Reader</a>.
          </div>
        )}
      </div>
    </div>
  );
}
